<script setup lang="ts">
  import { reactiveOmit } from '@vueuse/core'
  import { X } from 'lucide-vue-next'
  import {
    DialogClose,
    DialogContent,
    type DialogContentEmits,
    type DialogContentProps,
    DialogPortal,
    useForwardPropsEmits,
    VisuallyHidden,
    DialogTitle,
    DialogDescription
  } from 'reka-ui'
  import type { HTMLAttributes, StyleValue } from 'vue'
  import { cn } from '../../../lib/utils'
  import {
    SheetOverlay,
    type SheetVariants
  } from '@gat-ship/vue-components/sheet'
  import { testIds } from '@gat-ship/types'

  interface SheetContentProps extends DialogContentProps {
    class?: HTMLAttributes['class']
    side?: SheetVariants['side']
    /** Default false */
    closeOnOutsideClick?: boolean
    hideCloseButton?: boolean
    hideOverlay?: boolean
    overlayZIndex?: number
    maxWidth?: number
  }

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(defineProps<SheetContentProps>(), {
    side: 'right',
    overlayZIndex: 50,
    maxWidth: 400
  })
  const emits = defineEmits<
    DialogContentEmits & { 'click:close': [event: MouseEvent] }
  >()

  const delegatedProps = reactiveOmit(props, 'class', 'side')

  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  function onOutsideClick(e: Event) {
    if (!props.closeOnOutsideClick) {
      e.preventDefault()
    }
  }
</script>

<template>
  <DialogPortal>
    <SheetOverlay
      v-if="!hideOverlay"
      class="fixed inset-0 grid grid-cols-1 place-items-end bg-black/15 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      :style="{ zIndex: overlayZIndex }"
      @click.prevent
    />
    <DialogContent
      data-slot="sheet-content"
      :class="
        cn(
          'fixed inset-y-0 right-0 min-w-[320px] overflow-y-auto p-2 pl-4 transition ease-in-out data-[state=closed]:duration-100 data-[state=open]:duration-100 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          props.class
        )
      "
      v-bind="{ ...forwarded, ...$attrs }"
      :style="{ zIndex: overlayZIndex }"
      :aria-describedby="undefined"
      @interact-outside="onOutsideClick($event)"
    >
      <visually-hidden>
        <dialog-title>No title</dialog-title>
        <dialog-description>No description</dialog-description>
      </visually-hidden>
      <div
        class="bg-background relative flex min-h-full w-full flex-col rounded-lg shadow-md"
      >
        <slot />
      </div>

      <DialogClose
        v-if="!hideCloseButton"
        class="ring-offset-background focus:ring-ring focus:outline-hidden bg-background shadow-xs absolute left-2 top-1 flex size-6 items-center justify-center rounded-full border transition-opacity hover:cursor-pointer hover:opacity-100 focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
        :data-testid="testIds.common.dialog.closeX"
        @click="emits('click:close', $event)"
      >
        <X class="size-3.5" />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
